import React, {useMemo} from 'react';
import {SessionTag, TeamSession} from '../../../interfaces';
import {
  VictoryChart,
  VictoryBar,
  VictoryAxis,
  VictoryTooltip,
  VictoryStack,
  VictoryLine,
  VictoryScatter,
} from 'victory';
import {Positions, SessionTagCodes} from '../../../enums';
import {ORANGE_COLOR, SKY_BLUE, VIVID_VIOLET} from '../../../styles';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  getUniqueName,
  smallAxisStyleAxis,
  smallAxisStyleLabel,
  smallAxisStyleTickLabels,
  smallAxisStyleTicks,
  toolTipStyle,
} from '../../../services/graphService';
import {useTranslation} from 'react-i18next';
import {useResizeDetector} from 'react-resize-detector';
import GraphLegendItem from '../../shared/GraphLegendItem';
import PageSubtitle from '../../shared/PageSubtitle';
import {truncateString} from '../../../helpers';

const getPeriod = (sessionTags: SessionTag[]) => {
  if (
    sessionTags.filter(st => st.code === SessionTagCodes.period1).length > 0
  ) {
    return SessionTagCodes.period1;
  } else if (
    sessionTags.filter(st => st.code === SessionTagCodes.period2).length > 0
  ) {
    return SessionTagCodes.period2;
  } else if (
    sessionTags.filter(st => st.code === SessionTagCodes.period3).length > 0
  ) {
    return SessionTagCodes.period3;
  } else if (
    sessionTags.filter(st => st.code === SessionTagCodes.overtime).length > 0
  ) {
    return SessionTagCodes.overtime;
  }
};

// sort by forward/defense, then by player load
const playerLoadSort = (a: PlayerLoad, b: PlayerLoad) => {
  if (
    [Positions.center, Positions.leftwing, Positions.rightwing].indexOf(
      a.position as Positions,
    ) !== -1 &&
    [Positions.defense].indexOf(b.position as Positions) !== -1
  )
    return -1;
  if (
    [Positions.defense].indexOf(a.position as Positions) !== -1 &&
    [Positions.center, Positions.leftwing, Positions.rightwing].indexOf(
      b.position as Positions,
    ) !== -1
  )
    return 1;
  return (b.cumulative3DPlayerLoadV2 ?? 0) - (a.cumulative3DPlayerLoadV2 ?? 0);
};

interface PlayerLoad {
  name: string;
  cumulative3DPlayerLoadV2: number | null;
  activeTime: number | null;
  cumulative3DPlayerLoadV2Period1: number;
  cumulative3DPlayerLoadV2Period2: number;
  cumulative3DPlayerLoadV2Period3: number;
  cumulative3DPlayerLoadV2Overtime: number;
  position?: string;
  period?: string;
}

interface Props {
  teamSessions: TeamSession[];
}

const TeamSessionPlayerLoad: React.FC<Props> = ({teamSessions}) => {
  const {t} = useTranslation();

  const {width, ref} = useResizeDetector();

  const allPlayerNames = useMemo(() => {
    return (
      teamSessions
        .flatMap(ts => ts.playerSessions ?? [])
        // get unique players
        .filter(
          (value, index, self) =>
            self.findIndex(
              ps => ps.player?.profile?.id === value.player?.profile?.id,
            ) === index,
        )
        .map(ps => ({
          firstName: ps.player?.profile?.firstName ?? 'Unknown',
          lastName: ps.player?.profile?.lastName ?? 'Unknown',
          profileId: ps.player?.profile?.id ?? 0,
        }))
    );
  }, [teamSessions]);

  const data: PlayerLoad[] = useMemo(() => {
    const playerLoads = [...teamSessions]
      .filter(ts => !ts.sessionTags?.some(st => st.code === 'WARMUP'))
      .flatMap(
        ts =>
          (ts.playerSessions ?? []).map(ps => ({
            ...ps,
            period: getPeriod(ts.sessionTags ?? []),
          })) ?? [],
      )
      .filter(ps => (ps.activeTime ?? 0) > 0)
      .map(ps => ({
        cumulative3DPlayerLoadV2: (ps.cumulative3DPlayerLoadV2 ?? 0) / 100,
        activeTime: ps.activeTime ?? 0,
        cumulative3DPlayerLoadV2Period1:
          ps.period === SessionTagCodes.period1
            ? Math.round((ps.cumulative3DPlayerLoadV2 ?? 0) / 100)
            : 0,
        cumulative3DPlayerLoadV2Period2:
          ps.period === SessionTagCodes.period2
            ? Math.round((ps.cumulative3DPlayerLoadV2 ?? 0) / 100)
            : 0,
        cumulative3DPlayerLoadV2Period3:
          ps.period === SessionTagCodes.period3
            ? Math.round((ps.cumulative3DPlayerLoadV2 ?? 0) / 100)
            : 0,
        cumulative3DPlayerLoadV2Overtime:
          ps.period === SessionTagCodes.overtime
            ? Math.round((ps.cumulative3DPlayerLoadV2 ?? 0) / 100)
            : 0,
        position: ps.player?.profile?.position,
        period: ps.period,
        name: getUniqueName(
          {
            firstName: ps.player?.profile?.firstName ?? 'Unknown',
            lastName: ps.player?.profile?.lastName ?? 'Unknown',
            profileId: ps.player?.profile?.id ?? 0,
          },
          allPlayerNames,
        ),
      }))
      .filter(
        pl =>
          pl.cumulative3DPlayerLoadV2 > 0 &&
          [
            Positions.defense,
            Positions.center,
            Positions.leftwing,
            Positions.rightwing,
          ].includes(pl.position as Positions),
      )
      .map((pl: PlayerLoad) => ({
        name: pl.name,
        cumulative3DPlayerLoadV2: pl.cumulative3DPlayerLoadV2,
        activeTime: pl.activeTime,
        cumulative3DPlayerLoadV2Period1: pl.cumulative3DPlayerLoadV2Period1,
        cumulative3DPlayerLoadV2Period2: pl.cumulative3DPlayerLoadV2Period2,
        cumulative3DPlayerLoadV2Period3: pl.cumulative3DPlayerLoadV2Period3,
        cumulative3DPlayerLoadV2Overtime: pl.cumulative3DPlayerLoadV2Overtime,
        position: pl.position,
        period: pl.period,
      }))
      .reduce((acc: PlayerLoad[], curr) => {
        const existing = acc.find(a => a.name === curr.name);
        if (existing) {
          if (existing.cumulative3DPlayerLoadV2 === null) {
            existing.cumulative3DPlayerLoadV2 = 0;
          }
          existing.cumulative3DPlayerLoadV2 +=
            curr.cumulative3DPlayerLoadV2 ?? 0;
          if (existing.activeTime === null) {
            existing.activeTime = 0;
          }
          existing.activeTime += curr.activeTime ?? 0;
          if (curr.period === SessionTagCodes.period1) {
            existing.cumulative3DPlayerLoadV2Period1 +=
              curr.cumulative3DPlayerLoadV2 ?? 0;
          } else if (curr.period === SessionTagCodes.period2) {
            existing.cumulative3DPlayerLoadV2Period2 +=
              curr.cumulative3DPlayerLoadV2 ?? 0;
          } else if (curr.period === SessionTagCodes.period3) {
            existing.cumulative3DPlayerLoadV2Period3 +=
              curr.cumulative3DPlayerLoadV2 ?? 0;
          } else if (curr.period === SessionTagCodes.overtime) {
            existing.cumulative3DPlayerLoadV2Overtime +=
              curr.cumulative3DPlayerLoadV2 ?? 0;
          }
        } else {
          acc.push(curr);
        }
        return acc;
      }, [])
      .sort(playerLoadSort);

    // if less than 3 players add element to beginning and end of array
    if (playerLoads.length > 0) {
      playerLoads.unshift({
        name: 'begin',
        cumulative3DPlayerLoadV2: null,
        activeTime: null,
        cumulative3DPlayerLoadV2Period1: 0,
        cumulative3DPlayerLoadV2Period2: 0,
        cumulative3DPlayerLoadV2Period3: 0,
        cumulative3DPlayerLoadV2Overtime: 0,
        position: undefined,
        period: undefined,
      });
      playerLoads.push({
        name: 'end',
        cumulative3DPlayerLoadV2: null,
        activeTime: null,
        cumulative3DPlayerLoadV2Period1: 0,
        cumulative3DPlayerLoadV2Period2: 0,
        cumulative3DPlayerLoadV2Period3: 0,
        cumulative3DPlayerLoadV2Overtime: 0,
      });
    }

    return playerLoads;
  }, [allPlayerNames, teamSessions]);

  const allData = useMemo(() => {
    return data.map(pl => ({
      x: pl.name,
      y: pl.cumulative3DPlayerLoadV2,
      cumulative3DPlayerLoadV2: pl.cumulative3DPlayerLoadV2,
      loadPerMinute:
        (pl.activeTime ?? 0) > 0
          ? (pl.cumulative3DPlayerLoadV2 ?? 0) / ((pl.activeTime ?? 0) / 60)
          : null,
      position: pl.position,
      cumulative3DPlayerLoadV2Period1: pl.cumulative3DPlayerLoadV2Period1,
      cumulative3DPlayerLoadV2Period2: pl.cumulative3DPlayerLoadV2Period2,
      cumulative3DPlayerLoadV2Period3: pl.cumulative3DPlayerLoadV2Period3,
      cumulative3DPlayerLoadV2Overtime: pl.cumulative3DPlayerLoadV2Overtime,
    }));
  }, [data]);

  const period1Data = useMemo(() => {
    return data.map(pl => ({
      x: pl.name,
      y: pl.cumulative3DPlayerLoadV2Period1,
      position: pl.position,
    }));
  }, [data]);

  const period2Data = useMemo(() => {
    return data.map(pl => ({
      x: pl.name,
      y: pl.cumulative3DPlayerLoadV2Period2,
      position: pl.position,
    }));
  }, [data]);

  const period3Data = useMemo(() => {
    return data.map(pl => ({
      x: pl.name,
      y: pl.cumulative3DPlayerLoadV2Period3,
      position: pl.position,
    }));
  }, [data]);
  const overtimeData = useMemo(() => {
    return data.map(pl => ({
      x: pl.name,
      y: pl.cumulative3DPlayerLoadV2Overtime,
      position: pl.position,
    }));
  }, [data]);

  const activeTimeScaleFactor = useMemo(() => {
    // the largest load per minute
    const largestLoadPerMinute = allData
      .map(d => d.loadPerMinute ?? 0)
      .sort((a, b) => b - a)[0];
    const largestPlayerLoad = allData
      .map(d => d.cumulative3DPlayerLoadV2 ?? 0)
      .sort((a, b) => b - a)[0];
    if (largestLoadPerMinute && largestPlayerLoad) {
      return largestPlayerLoad / largestLoadPerMinute;
    }
    return 1;
  }, [allData]);

  const allDataLoadPerMinuteScaled = useMemo(() => {
    return allData.map(d => ({
      x: d.x,
      y: d.loadPerMinute
        ? (d.loadPerMinute ?? 0) * activeTimeScaleFactor
        : null,
    }));
  }, [allData, activeTimeScaleFactor]);

  const positionAverages = useMemo(() => {
    const players = data.filter(pl => pl.name !== 'begin' && pl.name !== 'end');
    const forwards = players.filter(pl =>
      [Positions.center, Positions.leftwing, Positions.rightwing].includes(
        pl.position as Positions,
      ),
    );
    const defense = players.filter(pl => pl.position === Positions.defense);

    const computeAvgs = (group: PlayerLoad[]) => {
      if (group.length === 0) {
        return {avgLoad: null, avgLoadPerMin: null};
      }
      const totalLoad = group.reduce(
        (sum, pl) => sum + (pl.cumulative3DPlayerLoadV2 ?? 0),
        0,
      );
      const perMinValues = group
        .map(pl =>
          (pl.activeTime ?? 0) > 0
            ? (pl.cumulative3DPlayerLoadV2 ?? 0) / ((pl.activeTime ?? 0) / 60)
            : null,
        )
        .filter((v): v is number => v !== null);
      const avgLoadPerMin =
        perMinValues.length > 0
          ? perMinValues.reduce((sum, v) => sum + v, 0) / perMinValues.length
          : null;
      return {
        avgLoad: totalLoad / group.length,
        avgLoadPerMin,
      };
    };

    return {
      all: computeAvgs(players),
      forwards: computeAvgs(forwards),
      defense: computeAvgs(defense),
    };
  }, [data]);

  if ((data ?? []).length === 0) {
    return null;
  }

  return (
    <Box sx={{mb: 5}}>
      <PageSubtitle>{t('statistics.playerLoad')}</PageSubtitle>
      <Box ref={ref}>
        <VictoryChart
          domainPadding={{x: 20}}
          width={width ?? undefined}
          padding={{top: 10, bottom: 90, left: 50, right: 50}}>
          <VictoryLine
            data={allDataLoadPerMinuteScaled}
            style={{
              data: {
                stroke: '#fff',
                strokeWidth: 2,
              },
            }}
          />
          <VictoryScatter
            data={allDataLoadPerMinuteScaled}
            size={2}
            style={{
              data: {
                stroke: '#fff',
                strokeWidth: 2,
                fill: '#fff',
              },
            }}
          />
          {(teamSessions ?? []).length > 1 && (
            <VictoryStack height={240}>
              {[0, 1, 2, 3].map(periodIndex => (
                <VictoryBar
                  barRatio={0.5}
                  key={periodIndex}
                  style={{
                    data: {
                      fill: ({datum}) => {
                        if (
                          [Positions.defense].indexOf(datum.position) !== -1
                        ) {
                          return `${VIVID_VIOLET}${(100 / 5) * (periodIndex + 1)}`;
                        } else if (
                          [
                            Positions.center,
                            Positions.leftwing,
                            Positions.rightwing,
                          ].indexOf(datum.position) !== -1
                        ) {
                          return `${SKY_BLUE}${(100 / 5) * (periodIndex + 1)}`;
                        }
                        return 'transparent';
                      },
                    },
                  }}
                  data={
                    periodIndex === 0
                      ? period1Data
                      : periodIndex === 1
                        ? period2Data
                        : periodIndex === 2
                          ? period3Data
                          : overtimeData
                  }
                />
              ))}
            </VictoryStack>
          )}
          <VictoryBar
            height={240}
            data={allData}
            barRatio={0.5}
            labels={({datum}) =>
              `${datum.x}` +
              `${datum.y ? `\n${t('statistics.playerLoadTooltipTotal', {value: datum.y.toFixed(1)})}` : ''}` +
              `${datum.loadPerMinute ? `\n${t('statistics.playerLoadTooltipPerMin', {value: datum.loadPerMinute?.toFixed(2)})}` : ''}` +
              `${datum.playerLoadPeriod1 ? `\n${t('statistics.playerLoadTooltipPeriod1', {value: datum.playerLoadPeriod1.toFixed(1)})}` : ''}` +
              `${datum.playerLoadPeriod2 ? `\n${t('statistics.playerLoadTooltipPeriod2', {value: datum.playerLoadPeriod2.toFixed(1)})}` : ''}` +
              `${datum.playerLoadPeriod3 ? `\n${t('statistics.playerLoadTooltipPeriod3', {value: datum.playerLoadPeriod3.toFixed(1)})}` : ''}` +
              `${datum.playerLoadOvertime ? `\n${t('statistics.playerLoadTooltipOvertime', {value: datum.playerLoadOvertime.toFixed(1)})}` : ''}`
            }
            labelComponent={<VictoryTooltip style={toolTipStyle} />}
            style={{
              data: {
                stroke: ({datum}) => {
                  if ([Positions.defense].indexOf(datum.position) !== -1) {
                    return VIVID_VIOLET;
                  } else if (
                    [
                      Positions.center,
                      Positions.leftwing,
                      Positions.rightwing,
                    ].indexOf(datum.position) !== -1
                  ) {
                    return SKY_BLUE;
                  } else {
                    return ORANGE_COLOR;
                  }
                },
                strokeWidth: 1,
                fill: ({datum}) => {
                  if ((teamSessions ?? []).length > 1) {
                    return 'transparent';
                  }
                  if ([Positions.defense].indexOf(datum.position) !== -1) {
                    return `${VIVID_VIOLET}33`;
                  } else if (
                    [
                      Positions.center,
                      Positions.leftwing,
                      Positions.rightwing,
                    ].indexOf(datum.position) !== -1
                  ) {
                    return `${SKY_BLUE}33`;
                  } else {
                    return `${ORANGE_COLOR}33`;
                  }
                },
              },
            }}
          />
          <VictoryAxis
            dependentAxis
            offsetX={70}
            label={t('general.load')}
            tickFormat={t => t}
            style={{
              ticks: smallAxisStyleTicks,
              axis: smallAxisStyleAxis,
              axisLabel: smallAxisStyleLabel,
              tickLabels: smallAxisStyleTickLabels,
            }}
          />
          <VictoryAxis
            dependentAxis
            orientation="right"
            offsetX={70}
            label={t('statistics.loadPerMinute')}
            tickFormat={t => (t / activeTimeScaleFactor).toFixed(1)}
            style={{
              ticks: smallAxisStyleTicks,
              axis: smallAxisStyleAxis,
              axisLabel: smallAxisStyleLabel,
              tickLabels: smallAxisStyleTickLabels,
            }}
          />
          <VictoryAxis
            tickFormat={t => {
              if (t === 'begin' || t === 'end') {
                return null;
              }
              return truncateString(t, 10);
            }}
            style={{
              axis: smallAxisStyleAxis,
              tickLabels: {
                ...smallAxisStyleTickLabels,
                angle: 45,
                textAnchor: 'start',
              },
            }}
          />
        </VictoryChart>
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 3,
          mt: 1,
        }}>
        {/* TeamSessionGroup Legend */}
        {(teamSessions ?? []).length > 1 && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'center',
            }}>
            <Box>
              <GraphLegendItem label="" backgroundColor="transparent" />
              <GraphLegendItem label={t('position.forwards')} />
              <GraphLegendItem label={t('position.defense')} />
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'center',
              }}>
              <Box sx={{ml: 2, mr: 2}}>
                <GraphLegendItem label={t('teamShiftUtilization.period1')} />
                <GraphLegendItem
                  borderColor={SKY_BLUE}
                  backgroundColor={`${SKY_BLUE}20`}
                />
                <GraphLegendItem
                  borderColor={VIVID_VIOLET}
                  backgroundColor={`${VIVID_VIOLET}20`}
                />
              </Box>
              <Box sx={{mr: 2}}>
                <GraphLegendItem label={t('teamShiftUtilization.period2')} />
                <GraphLegendItem
                  borderColor={SKY_BLUE}
                  backgroundColor={`${SKY_BLUE}40`}
                />
                <GraphLegendItem
                  borderColor={VIVID_VIOLET}
                  backgroundColor={`${VIVID_VIOLET}40`}
                />
              </Box>
              <Box sx={{mr: 2}}>
                <GraphLegendItem label={t('teamShiftUtilization.period3')} />
                <GraphLegendItem
                  borderColor={SKY_BLUE}
                  backgroundColor={`${SKY_BLUE}60`}
                />
                <GraphLegendItem
                  borderColor={VIVID_VIOLET}
                  backgroundColor={`${VIVID_VIOLET}60`}
                />
              </Box>
              {/* Only show overtime if there is an overtime period */}
              {(overtimeData ?? []).some(d => d.y > 0) && (
                <Box sx={{mr: 2}}>
                  <GraphLegendItem label={t('teamShiftUtilization.overtime')} />
                  <GraphLegendItem
                    borderColor={SKY_BLUE}
                    backgroundColor={`${SKY_BLUE}80`}
                  />
                  <GraphLegendItem
                    borderColor={VIVID_VIOLET}
                    backgroundColor={`${VIVID_VIOLET}80`}
                  />
                </Box>
              )}
            </Box>
          </Box>
        )}
        {/* Single TeamSession Legend */}
        {(teamSessions ?? []).length === 1 && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 2,
            }}>
            <GraphLegendItem
              borderColor={SKY_BLUE}
              label={t('position.forwards')}
            />
            <GraphLegendItem
              borderColor={VIVID_VIOLET}
              backgroundColor={`${VIVID_VIOLET}33`}
              label={t('position.defense')}
            />
          </Box>
        )}
        {/* Position averages */}
        <TableContainer component={Paper} sx={{width: 'auto'}}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell />
                <TableCell align="right">{t('position.all')}</TableCell>
                <TableCell align="right">{t('position.forwards')}</TableCell>
                <TableCell align="right">{t('position.defense')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(
                [
                  ['avgLoad', t('statistics.playerLoadAvg'), 1],
                  ['avgLoadPerMin', t('statistics.playerLoadPerMinAvg'), 2],
                ] as const
              ).map(([metricKey, label, decimals]) => (
                <TableRow key={metricKey}>
                  <TableCell>{label}</TableCell>
                  {(['all', 'forwards', 'defense'] as const).map(posKey => (
                    <TableCell key={posKey} align="right">
                      {positionAverages[posKey][metricKey] !== null
                        ? positionAverages[posKey][metricKey]?.toFixed(decimals)
                        : '--'}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
};

export default TeamSessionPlayerLoad;
